import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import api from '../services/api';

const RetailerDetails = () => {
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  // Debug activeImageIndex changes
  useEffect(() => {
    console.log('🖼️ Active image index changed to:', activeImageIndex);
  }, [activeImageIndex]);

  // Fetch retailer details from API
  const { data: retailerResponse, isLoading, error } = useQuery(
    ['retailerDetails', retailerId],
    async () => {
      const response = await api.get(`/retailers/${retailerId}/details`);
      return response.data;
    },
    {
      enabled: !!retailerId,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  const retailerData = retailerResponse?.data;

  // Debug logging for retailer data and logo
  console.log('🖼️ RetailerDetails Debug - Full API Response:', retailerResponse);
  console.log('🖼️ RetailerDetails Debug - Retailer Data:', retailerData);
  if (retailerData) {
    console.log('🖼️ RetailerDetails Debug - Logo URL (logo_url):', retailerData.logo_url);
    console.log('🖼️ RetailerDetails Debug - Logo (logo):', retailerData.logo);
    console.log('🖼️ RetailerDetails Debug - Logo URL Type:', typeof retailerData.logo);
    console.log('🖼️ RetailerDetails Debug - Logo URL Empty:', !retailerData.logo);
    console.log('🖼️ RetailerDetails Debug - All Retailer Keys:', Object.keys(retailerData));
    console.log('🖼️ RetailerDetails Debug - Store Images:', retailerData.inStoreImages);
    console.log('🖼️ RetailerDetails Debug - Store Images Length:', retailerData.inStoreImages?.length);
    console.log('🖼️ RetailerDetails Debug - Store Images Type:', typeof retailerData.inStoreImages);
    console.log('🖼️ RetailerDetails Debug - Store Images Is Array:', Array.isArray(retailerData.inStoreImages));
    if (retailerData.inStoreImages && retailerData.inStoreImages.length > 0) {
      console.log('🖼️ RetailerDetails Debug - Store Images Content:', JSON.stringify(retailerData.inStoreImages, null, 2));
    }
  }

  const handleBackClick = () => {
    navigate(-1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading retailer details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !retailerData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Retailer Not Found</h2>
          <p className="text-gray-600 mb-4">The retailer you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBackClick}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Discovery
          </button>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    if (retailerData.inStoreImages && retailerData.inStoreImages.length > 0) {
      setActiveImageIndex((prev) => {
        const next = (prev + 1) % retailerData.inStoreImages.length;
        console.log('🖼️ Next image clicked - Current:', prev, 'Next:', next, 'Total images:', retailerData.inStoreImages.length);
        return next;
      });
    }
  };

  const prevImage = () => {
    if (retailerData.inStoreImages && retailerData.inStoreImages.length > 0) {
      setActiveImageIndex((prev) => {
        const next = (prev - 1 + retailerData.inStoreImages.length) % retailerData.inStoreImages.length;
        console.log('🖼️ Previous image clicked - Current:', prev, 'Next:', next, 'Total images:', retailerData.inStoreImages.length);
        return next;
      });
    }
  };

  // Professional pie chart component
  const PieChart = ({ data, title }) => {
    const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
    const [hoveredSegment, setHoveredSegment] = useState(null);
    let cumulativePercentage = 0;
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </div>
        
        <div className="relative w-64 h-64 mx-auto mb-8">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {data.map((item, index) => {
              // Handle single item with 100% - draw a full circle
              if (data.length === 1 && item.percentage === 100) {
                return (
                  <circle
                    key={index}
                    cx="50"
                    cy="50"
                    r="40"
                    fill={item.color}
                    stroke="white"
                    strokeWidth="2"
                    className={`cursor-pointer transition-all duration-200 ${
                      hoveredSegment === item.name ? 'opacity-100 scale-105' : 'opacity-80 hover:opacity-100'
                    }`}
                    onMouseEnter={(e) => {
                      setHoveredSegment(item.name);
                      setTooltip({
                        visible: true,
                        content: `${item.name}: ${item.percentage}%`,
                        x: e.clientX,
                        y: e.clientY
                      });
                    }}
                    onMouseLeave={() => {
                      setHoveredSegment(null);
                      setTooltip({ visible: false, content: '', x: 0, y: 0 });
                    }}
                  />
                );
              }
              
              const startAngle = cumulativePercentage * 3.6; // Convert percentage to degrees
              const endAngle = (cumulativePercentage + item.percentage) * 3.6;
              const largeArcFlag = item.percentage > 50 ? 1 : 0;
              
              const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
              const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
              const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
              const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
              
              const pathData = [
                `M 50 50`,
                `L ${x1} ${y1}`,
                `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                'Z'
              ].join(' ');
              
              cumulativePercentage += item.percentage;
              
              return (
                <path
                  key={index}
                  d={pathData}
                  fill={item.color}
                  stroke="white"
                  strokeWidth="2"
                  className={`cursor-pointer transition-all duration-200 ${
                    hoveredSegment === item.name ? 'opacity-100 scale-105' : 'opacity-80 hover:opacity-100'
                  }`}
                  onMouseEnter={(e) => {
                    setHoveredSegment(item.name);
                    setTooltip({
                      visible: true,
                      content: `${item.name}: ${item.percentage}%`,
                      x: e.clientX,
                      y: e.clientY
                    });
                  }}
                  onMouseLeave={() => {
                    setHoveredSegment(null);
                    setTooltip({ visible: false, content: '', x: 0, y: 0 });
                  }}
                />
              );
            })}
          </svg>
          
          {/* Tooltip */}
          {tooltip.visible && (
            <div
              className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium pointer-events-none"
              style={{
                left: tooltip.x - 50,
                top: tooltip.y - 40,
                transform: 'translateX(-50%)'
              }}
            >
              {tooltip.content}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          {data.map((item, index) => (
            <div 
              key={index} 
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                hoveredSegment === item.name 
                  ? 'bg-blue-50 border-2 border-blue-200 shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
              onMouseEnter={() => setHoveredSegment(item.name)}
              onMouseLeave={() => setHoveredSegment(null)}
            >
              <div className="flex items-center">
                <div 
                  className={`w-4 h-4 rounded-full mr-4 shadow-sm transition-all duration-200 ${
                    hoveredSegment === item.name ? 'scale-110' : ''
                  }`}
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className={`font-medium transition-all duration-200 ${
                  hoveredSegment === item.name ? 'text-blue-700 font-semibold' : 'text-gray-700'
                }`}>
                  {item.name}
                </span>
              </div>
              <div className="text-right">
                <span className={`font-bold text-lg transition-all duration-200 ${
                  hoveredSegment === item.name ? 'text-blue-700' : 'text-gray-900'
                }`}>
                  {item.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Clean Enterprise Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-8 py-4">
          <div className="flex items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back to Discovery</span>
            </button>
            <div className="ml-8">
              <h1 className="text-2xl font-bold text-gray-900">Retailer Analysis</h1>
              <p className="text-sm text-gray-600 mt-1">Comprehensive insights and market data</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-screen">
        {/* Left Sidebar - Retailer Profile */}
        <div className="w-80 bg-gradient-to-br from-purple-600 to-purple-700 flex-shrink-0 p-8 text-white">
          <div className="flex items-center mb-10">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mr-4 shadow-lg overflow-hidden">
              {(() => {
                console.log('🖼️ RetailerDetails Logo Render Debug - About to render logo for:', retailerData?.name);
                console.log('🖼️ RetailerDetails Logo Render Debug - Logo exists:', !!retailerData?.logo);
                console.log('🖼️ RetailerDetails Logo Render Debug - Logo value:', retailerData?.logo);
                
                if (retailerData?.logo) {
                  console.log('🖼️ RetailerDetails Logo Render Debug - Rendering IMG tag with src:', retailerData.logo);
                  return (
                    <img 
                      src={retailerData.logo} 
                      alt={`${retailerData.name} logo`}
                      className="w-full h-full object-cover rounded-xl"
                      onLoad={() => {
                        console.log('🖼️ RetailerDetails Logo Render Debug - Image loaded successfully for:', retailerData.name);
                      }}
                      onError={(e) => {
                        console.error('🖼️ RetailerDetails Logo Render Debug - Image failed to load for:', retailerData.name);
                        console.error('🖼️ RetailerDetails Logo Render Debug - Failed URL:', retailerData.logo);
                        console.error('🖼️ RetailerDetails Logo Render Debug - Error event:', e);
                        // Fallback to initials if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  );
                } else {
                  console.log('🖼️ RetailerDetails Logo Render Debug - No logo, showing initials for:', retailerData?.name);
                  return null;
                }
              })()}
              <span 
                className="text-purple-600 font-bold text-xl"
                style={{ display: retailerData?.logo ? 'none' : 'flex' }}
              >
                {retailerData?.name?.split(' ').map(word => word.charAt(0)).join('').substring(0, 2)}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold">{retailerData.name}</h2>
              <p className="text-purple-100 text-sm">Retail Partner Profile</p>
            </div>
          </div>
          
          <div className="space-y-5">
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">CHAIN TYPE</span>
                <span className="font-bold text-lg">{retailerData.chainType}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">STORE COUNT</span>
                <span className="font-bold text-lg">{retailerData.storeCount}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">BUSINESS MODEL</span>
                <span className="font-bold text-lg">{retailerData.businessModel}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">FORMAT</span>
                <span className="font-bold text-lg">{retailerData.format}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">PAYMENT TERM</span>
                <span className="font-bold text-lg">{retailerData.paymentTerm}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">PAYMENT TIME</span>
                <span className="font-bold text-lg">{retailerData.paymentTime}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">STATE/UT PRESENCE</span>
                <span className="font-bold text-lg">{retailerData.statePresence}</span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 text-sm font-medium">CITY PRESENCE</span>
                <span className="font-bold text-lg">{retailerData.cityPresence}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-8">
            {/* Match Score Banner */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Partnership Compatibility Analysis
                </h2>
                <div className="text-xl text-gray-700 leading-relaxed">
                  FIT Score with <span className="font-semibold text-gray-900">{retailerData.name}</span> is{' '}
                  <span className="text-orange-500 text-4xl font-bold">{retailerData.matchScore}%</span>
                </div>
                <div className="text-lg text-gray-600 mt-4">
                  Total Category Size (in INR): <span className="font-semibold text-gray-900">{retailerData.categorySize}</span> • 
                  Brand Share in Category: <span className="font-semibold text-gray-900">{retailerData.categoryPercentage}</span>
                </div>
              </div>
            </div>

            {/* Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <PieChart data={retailerData.subCategoryShare} title="SUBCATEGORY DISTRIBUTION" />
              <PieChart data={retailerData.brandShare} title="BRAND MARKET SHARE" />
            </div>

            {/* Store Layout Analysis */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Store Layout Analysis</h3>
                <p className="text-gray-600">Visual representation of product placement and store environment</p>
              </div>
              
              {retailerData.inStoreImages && retailerData.inStoreImages.length > 0 ? (
                <div className="relative">
                  <div className="flex items-center justify-center">
                    <button
                      onClick={prevImage}
                      className="absolute left-4 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 border border-gray-200"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <div className="relative w-96 h-64 mx-auto overflow-hidden rounded-xl border-2 border-blue-500 shadow-lg">
                      <div className="flex h-full transition-transform duration-300 ease-in-out" 
                           style={{ transform: `translateX(-${activeImageIndex * 100}%)` }}>
                        {retailerData.inStoreImages.map((image, index) => (
                          <div
                            key={index}
                            className="flex-shrink-0 w-96 h-64"
                          >
                            <img
                              src={image}
                              alt={`Store layout ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.error('Failed to load store image:', image);
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <button
                      onClick={nextImage}
                      className="absolute right-4 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 border border-gray-200"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Image indicators */}
                  <div className="flex justify-center mt-6 space-x-3">
                    {retailerData.inStoreImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-200 ${
                          index === activeImageIndex ? 'bg-blue-500' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">🏪</div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Store Images Not Available</h4>
                  <p className="text-gray-500">Store layout images for this retailer are not currently available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerDetails;
